// frontend/src/pages/reports/StudentPrintReportPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Table, Space, message, Typography, Select, Card, Row, Col, Avatar, Tooltip } from 'antd';
import { UserOutlined, PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { getAllStudents } from '../../services/studentService';
import { getAllClasses } from '../../services/classService';
import { imageBaseUrl } from '../../services/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import useResponsive from '../../hooks/useResponsive';
import femaleAvatar from '../../assets/female.png';
import maleAvatar from '../../assets/male.png';
import schoolLogo from '../../assets/logo.png';

const { Title, Text } = Typography;
const { Option } = Select;

const StudentPrintReportPage = () => {
    const { currentYear } = useAcademicYear();
    const { isMobile } = useResponsive();
    const [students, setStudents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const data = await getAllClasses();
                setClasses(data);
            } catch (error) {
                message.error('Failed to load classes.');
            }
        };
        fetchClasses();
    }, []);

    const fetchStudents = useCallback(async () => {
        if (!selectedClass) {
            setStudents([]);
            return;
        }
        setLoading(true);
        try {
            const data = await getAllStudents({ classId: selectedClass });
            // Students are already flattened by backend
            setStudents(data.map(s => ({ ...s, key: s.id })));
        } catch (error) {
            message.error('Failed to load students.');
        } finally {
            setLoading(false);
        }
    }, [selectedClass]);

    useEffect(() => {
        if (currentYear && selectedClass) {
            fetchStudents();
        }
    }, [currentYear, selectedClass, fetchStudents]);

    const getAvatarSrc = (item) => {
        if (!item) return maleAvatar;
        return item.profile_picture_url
            ? `${imageBaseUrl}${item.profile_picture_url}`
            : (item.gender && item.gender.toLowerCase() === 'female' ? femaleAvatar : maleAvatar);
    };

    const loadImage = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(e);
            img.src = url;
        });
    };

    const getRoundedImage = (img, targetW_mm, targetH_mm, radius_mm) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const pxPerMm = 12; 
        const w = targetW_mm * pxPerMm;
        const h = targetH_mm * pxPerMm;
        const r = radius_mm * pxPerMm;
        canvas.width = w;
        canvas.height = h;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(0, 0, w, h, r);
        else {
            ctx.moveTo(r, 0); ctx.arcTo(w, 0, w, h, r); ctx.arcTo(w, h, 0, h, r); ctx.arcTo(0, h, 0, 0, r); ctx.arcTo(0, 0, w, 0, r);
        }
        ctx.clip();
        const imgRatio = img.width / img.height;
        const targetRatio = w / h;
        let drawW, drawH, drawX, drawY;
        if (imgRatio > targetRatio) {
            drawH = h; drawW = h * imgRatio; drawX = (w - drawW) / 2; drawY = 0;
        } else {
            drawW = w; drawH = w / imgRatio; drawX = 0; drawY = (h - drawH) / 2;
        }
        ctx.drawImage(img, drawX, drawY, drawW, drawH);
        return canvas.toDataURL('image/png');
    };

    const handlePrintIDCard = async (studentOrList) => {
        const items = Array.isArray(studentOrList) ? studentOrList : [studentOrList];
        if (items.length === 0) return;

        try {
            const totalW = 57.3;
            const totalH = 89.3;
            const doc = new jsPDF('p', 'mm', [totalW, totalH]);
            const blueColor = [45, 66, 154];
            const startX = 0;

            for (let i = 0; i < items.length; i++) {
                const student = items[i];
                if (i > 0) doc.addPage([totalW, totalH]);

                let yPos = 0;
                doc.setFillColor(...blueColor);
                doc.rect(startX, yPos, totalW, 10, 'F');
                
                const schoolNameText = 'KALLADI ABDU HAJI HSS';
                doc.setFontSize(7.5);
                doc.setFont(undefined, 'bold');
                const textW = doc.getTextWidth(schoolNameText);
                const logoW = 6;
                const headerGap = 2;
                const totalHeaderContentW = logoW + headerGap + textW;
                const headerStartX = startX + (totalW - totalHeaderContentW) / 2;

                try {
                    doc.addImage(schoolLogo, 'PNG', headerStartX, yPos + 2, logoW, logoW);
                } catch (e) {
                    doc.setFillColor(255, 255, 255);
                    doc.circle(headerStartX + logoW/2, yPos + 5, logoW/2, 'F');
                }
                
                doc.setTextColor(255, 255, 255);
                doc.text(schoolNameText, headerStartX + logoW + headerGap, yPos + 6);
                yPos += 10;

                const footerH = 9;
                const mainAreaH = totalH - 10 - footerH;
                doc.setFillColor(255, 255, 255);
                doc.rect(startX, yPos, totalW, mainAreaH, 'F');
                
                const photoW = 24;
                const photoH = 28;
                const photoX = startX + (totalW - photoW) / 2;
                const photoY = yPos + 2.5;
                const cornerRadius = 1.5;
                
                if (student.profile_picture_url) {
                    try {
                        const img = await loadImage(`${imageBaseUrl}${student.profile_picture_url}`);
                        const roundedImgData = getRoundedImage(img, photoW, photoH, cornerRadius);
                        doc.addImage(roundedImgData, 'PNG', photoX, photoY, photoW, photoH);
                    } catch (e) {
                        doc.setDrawColor(230); doc.roundedRect(photoX, photoY, photoW, photoH, cornerRadius, cornerRadius, 'D');
                        doc.setFontSize(5); doc.setTextColor(150); doc.text('PHOTO', photoX + photoW/2, photoY + photoH/2, { align: 'center' });
                    }
                } else {
                    doc.setDrawColor(230); doc.roundedRect(photoX, photoY, photoW, photoH, cornerRadius, cornerRadius, 'D');
                    doc.setFontSize(5); doc.setTextColor(150); doc.text('NO PHOTO', photoX + photoW/2, photoY + photoH/2, { align: 'center' });
                }

                doc.setDrawColor(230); doc.setLineWidth(0.3); doc.roundedRect(photoX, photoY, photoW, photoH, cornerRadius, cornerRadius, 'D');

                doc.setTextColor(100); doc.setFontSize(7); doc.setFont(undefined, 'bold');
                doc.text(currentYear?.year_name || '2026-27', startX + totalW - 4, photoY + photoH/2, { angle: -90, align: 'center' });

                let nameY = photoY + photoH + 5.5;
                doc.setTextColor(...blueColor); doc.setFontSize(10); doc.setFont(undefined, 'bold');
                const name = (student.student_name || '').toUpperCase();
                let nameFontSize = 10; doc.setFontSize(nameFontSize);
                while (doc.getTextWidth(name) > totalW - 10 && nameFontSize > 6.5) {
                    nameFontSize -= 0.5; doc.setFontSize(nameFontSize);
                }
                doc.text(name, startX + totalW / 2, nameY, { align: 'center' });

                doc.setTextColor(50); doc.setFontSize(6.5); doc.setFont(undefined, 'normal');
                let detailY = nameY + 4.5;
                const lineH = 3.5;
                const labelX = startX + 5;
                const colonX = startX + 22;
                const valueX = startX + 24;

                const details = [
                    ['Admission No', student.admission_no || 'N/A'],
                    ['Roll No', student.roll_no || 'N/A'],
                    ['Father Name', student.parent_name || 'N/A'],
                    ['Date of Birth', student.dob ? dayjs(student.dob).format('DD-MM-YYYY') : 'N/A'],
                    ['Class', student.class_name || 'N/A'],
                ];

                details.forEach(([label, value]) => {
                    doc.setFont(undefined, 'bold'); doc.text(label, labelX, detailY);
                    doc.text(':', colonX, detailY);
                    doc.setFont(undefined, 'normal'); doc.text(String(value), valueX, detailY);
                    detailY += lineH;
                });

                doc.setFont(undefined, 'bold'); doc.text('Address', labelX, detailY); doc.text(':', colonX, detailY);
                doc.setFont(undefined, 'normal');
                const addr = student.address || 'N/A';
                const splitAddr = doc.splitTextToSize(addr, totalW - 29);
                doc.text(splitAddr, valueX, detailY);
                const addrLines = Array.isArray(splitAddr) ? splitAddr.length : 1;
                detailY += Math.max(1.8, addrLines) * lineH;

                doc.setFont(undefined, 'bold'); doc.text('Phone', labelX, detailY); doc.text(':', colonX, detailY);
                doc.setFont(undefined, 'normal'); doc.text(String(student.phone_number || 'N/A'), valueX, detailY);

                const footerY = totalH - footerH;
                doc.setFillColor(...blueColor); doc.rect(startX, footerY, totalW, footerH, 'F');
                doc.setTextColor(255, 255, 255); doc.setFontSize(5); doc.setFont(undefined, 'normal');
                doc.text('KAHHSS, KOTTOPADAM, MANNARKKAD, INDIA, 678583', startX + totalW / 2, footerY + 3.5, { align: 'center' });
                doc.text('Phone: 04924 262 033, Mail: kahhss9128@gmail.com', startX + totalW / 2, footerY + 6.5, { align: 'center' });
            }
            doc.save(items.length === 1 ? `${items[0].student_name}_ID_Card.pdf` : `Bulk_ID_Cards.pdf`);
        } catch (error) {
            console.error(error); message.error('Failed to generate ID info PDF.');
        }
    };

    const handlePrintFull = async (studentOrList) => {
        const items = Array.isArray(studentOrList) ? studentOrList : [studentOrList];
        if (items.length === 0) return;

        try {
            const doc = new jsPDF('p', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();

            for (let i = 0; i < items.length; i++) {
                const student = items[i];
                if (i > 0) doc.addPage();

                let yPos = 15;
                doc.setFontSize(18); doc.setFont(undefined, 'bold');
                doc.text('STUDENT INFORMATION DETAILS', pageWidth / 2, yPos, { align: 'center' });
                yPos += 10;

                if (student.profile_picture_url) {
                    try {
                        const img = await loadImage(`${imageBaseUrl}${student.profile_picture_url}`);
                        doc.addImage(img, 'JPEG', pageWidth - 45, yPos, 30, 40);
                    } catch (e) { }
                }

                doc.setFontSize(12);
                const data = [
                    ['Student Name', student.student_name],
                    ['Admission No', student.admission_no],
                    ['Class', student.class_name],
                    ['Roll No', student.roll_no || 'N/A'],
                    ['Date of Birth', student.dob ? dayjs(student.dob).format('DD MMMM YYYY') : 'N/A'],
                    ['Gender', student.gender || 'N/A'],
                    ['Phone Number', student.phone_number || 'N/A'],
                    ['Address', student.address || 'N/A'],
                    ['Father Name', student.parent_name || 'N/A'],
                    ['Mother Name', student.mother_name || 'N/A'],
                    ['Guardian Name', student.guardian_name || 'N/A'],
                    ['Guardian Phone', student.guardian_phone_number || 'N/A'],
                    ['Religion & Caste', student.religion_caste || 'N/A'],
                    ['Aadhar No', student.aadhar_no || 'N/A'],
                    ['SSLC Reg No', student.sslc_reg_no || 'N/A'],
                    ['SSLC School', student.sslc_school || 'N/A']
                ];

                autoTable(doc, {
                    startY: yPos, margin: { right: 50 }, body: data, theme: 'striped', styles: { fontSize: 10 }
                });

                yPos = doc.lastAutoTable.finalY + 10;
                doc.setFont(undefined, 'bold'); doc.text('SSLC Grades', 15, yPos); yPos += 5;
                const grades = [
                    ['Physics', student.sslc_physics || 'N/A', 'Chemistry', student.sslc_chemistry || 'N/A'],
                    ['Maths', student.sslc_maths || 'N/A', 'Biology', student.sslc_biology || 'N/A'],
                    ['Social Studies', student.sslc_social_studies || 'N/A', 'English', student.sslc_english || 'N/A']
                ];
                autoTable(doc, { startY: yPos, body: grades, theme: 'grid', styles: { fontSize: 10 } });
            }
            doc.save(items.length === 1 ? `${items[0].student_name}_Details.pdf` : `Bulk_Student_Details.pdf`);
        } catch (error) {
            console.error(error); message.error('Failed to generate full details PDF.');
        }
    };

    const columns = [
        {
            title: 'Photo', key: 'photo', width: 80, align: 'center',
            render: (_, record) => ( <Avatar src={getAvatarSrc(record)} icon={<UserOutlined />} /> )
        },
        { title: 'Adm No', dataIndex: 'admission_no', key: 'admission_no' },
        { title: 'Roll No', dataIndex: 'roll_no', key: 'roll_no', render: (text) => text || 'N/A' },
        { title: 'Name', dataIndex: 'student_name', key: 'student_name' },
        { title: 'Phone', dataIndex: 'phone_number', key: 'phone_number' },
        {
            title: 'Actions', key: 'action', align: 'center',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Print ID Info">
                        <Button icon={<PrinterOutlined />} onClick={() => handlePrintIDCard(record)} />
                    </Tooltip>
                    <Tooltip title="Print Full Details">
                        <Button icon={<DownloadOutlined />} onClick={() => handlePrintFull(record)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const onSelectChange = (newSelectedRowKeys) => { setSelectedRowKeys(newSelectedRowKeys); };
    const rowSelection = { selectedRowKeys, onChange: onSelectChange };
    const hasSelected = selectedRowKeys.length > 0;
    const selectedRecords = students.filter(s => selectedRowKeys.includes(s.id));

    return (
        <div style={{ padding: '32px', backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
            <div style={{ marginBottom: 32 }}>
                <Title level={2} style={{ fontWeight: 800, margin: 0 }}>Student Document Center</Title>
                <Text type="secondary" style={{ fontSize: 16 }}>
                    Generate professional ID information cards and comprehensive student profiles.
                </Text>
            </div>
            
            <Card style={{ marginBottom: 24, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Row gutter={24} align="middle">
                    <Col xs={24} md={8}>
                        <Text strong>Select Class: </Text>
                        <Select placeholder="Select a class" style={{ width: '100%', marginTop: 8 }} onChange={setSelectedClass} value={selectedClass}>
                            {classes.map(c => ( <Option key={c.id} value={c.id}>{c.name}</Option> ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={16} style={{ textAlign: isMobile ? 'left' : 'right', marginTop: isMobile ? 16 : 0 }}>
                        <Space wrap>
                            {hasSelected ? (
                                <>
                                    <Text strong>{selectedRowKeys.length} selected</Text>
                                    <Button type="primary" icon={<PrinterOutlined />} onClick={() => handlePrintIDCard(selectedRecords)}>Print ID Info</Button>
                                    <Button type="primary" icon={<DownloadOutlined />} onClick={() => handlePrintFull(selectedRecords)}>Print Full Details</Button>
                                </>
                            ) : (
                                students.length > 0 && (
                                    <>
                                        <Button icon={<PrinterOutlined />} onClick={() => handlePrintIDCard(students)}>Print All ID Info</Button>
                                        <Button icon={<DownloadOutlined />} onClick={() => handlePrintFull(students)}>Print All Full Details</Button>
                                    </>
                                )
                            )}
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Table
                rowSelection={rowSelection} dataSource={students} columns={columns} loading={loading} bordered pagination={false}
                style={{ backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                locale={{ emptyText: selectedClass ? 'No students found for this class' : 'Please select a class to view students' }}
            />
        </div>
    );
};

export default StudentPrintReportPage;
